import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, ChefHat, Clock, Camera, Check, ShoppingCart, Share2, Plus, Copy } from "lucide-react";
import { toast } from "sonner";
import { getRecipes } from "@/lib/api";
import { haptic } from "@/lib/haptics";
import RecipeShareModal from "@/components/RecipeShareModal";

export default function Recipes() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(location.state || null);
  const [loading, setLoading] = useState(!location.state);
  const [open, setOpen] = useState(0);
  const [shareRecipe, setShareRecipe] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [listOpen, setListOpen] = useState(false);

  useEffect(() => {
    if (data) return;
    getRecipes(id)
      .then(setData)
      .catch(() => setData(false))
      .finally(() => setLoading(false));
  }, [id, data]);

  if (loading) {
    return (
      <div className="app-shell min-h-[100dvh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 spin-slow text-[#4a5c62]" />
      </div>
    );
  }

  const result = data && data.result;
  if (!data || !result) {
    return (
      <div className="app-shell min-h-[100dvh] flex flex-col items-center justify-center px-8 text-center gap-4">
        <p className="font-serif text-3xl">These recipes have gone off.</p>
        <button onClick={() => navigate("/cook")} className="btn-ink rounded-sm px-6 py-3">Try another photo</button>
      </div>
    );
  }

  if (result.readable === false) {
    return (
      <div className="app-shell min-h-[100dvh] flex flex-col items-center justify-center px-8 text-center gap-5" data-testid="cook-reshoot">
        <Camera className="w-10 h-10 text-[#4a5c62]" strokeWidth={1.2} />
        <p className="font-serif text-3xl leading-tight">{result.reshoot_reason || "Couldn't read the fridge. Give it another go."}</p>
        <button onClick={() => navigate("/capture?mode=cook&diet=any&effort=quick")} className="btn-ink rounded-sm px-6 py-4 flex items-center gap-2">
          Take it again <Camera className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>
    );
  }

  const recipes = result.recipes || [];
  const countWord = { 1: "One thing", 2: "Two things", 3: "Three things" }[recipes.length] || `${recipes.length} things`;

  const toggleSel = (i) => {
    haptic(6);
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(i) ? n.delete(i) : n.add(i);
      return n;
    });
  };
  const shoppingItems = (() => {
    const map = new Map(); // normalized key -> shortest display
    [...selected].forEach((i) =>
      (recipes[i]?.need || []).forEach((raw) => {
        const item = String(raw).trim();
        if (!item) return;
        const key = item.toLowerCase().replace(/\s+or\s+.*$/, "").replace(/[^a-z0-9 ]/g, "").trim() || item.toLowerCase();
        const existing = map.get(key);
        if (!existing || item.length < existing.length) map.set(key, item);
      })
    );
    return [...map.values()];
  })();
  const copyList = async () => {
    haptic(10);
    const text = "Shopping list — What's in the Fridge\n" + shoppingItems.map((x) => `• ${x}`).join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast("Shopping list copied.");
    } catch {
      toast("Couldn't copy — long-press to select.");
    }
  };

  return (
    <div className="app-shell min-h-[100dvh] px-6 pt-[calc(env(safe-area-inset-top)+1.5rem)] pb-[calc(env(safe-area-inset-bottom)+3rem)]" data-testid="recipes-screen">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <ChefHat className="w-5 h-5 text-[#4a5c62]" strokeWidth={1.5} />
          <span className="text-xs uppercase tracking-[0.28em] font-semibold text-[#4a5c62]">On the menu</span>
        </div>
        <button data-testid="recipes-close-btn" onClick={() => navigate("/")} className="w-9 h-9 rounded-full glass flex items-center justify-center">
          <X className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>

      <motion.h1 className="font-serif text-4xl font-black tracking-tighter leading-[0.95]" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}>
        {countWord} worth making.
      </motion.h1>
      {result.intro_copy && <p className="font-serif text-lg italic text-[#4a5c62] mt-3">{result.intro_copy}</p>}

      {result.spotted?.length > 0 && (
        <div className="mt-5" data-testid="spotted-ingredients">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4a5c62] mb-2">Spotted in there</p>
          <div className="flex flex-wrap gap-2">
            {result.spotted.map((s) => (
              <span key={s} className="text-xs glass rounded-full px-3 py-1.5 text-[#16242a]">{s}</span>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 flex flex-col gap-3">
        {recipes.map((r, i) => (
          <motion.div
            key={`${r.title}-${i}`}
            className="glass rounded-sm overflow-hidden"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.08 }}
            data-testid={`recipe-card-${i}`}
          >
            <button
              onClick={() => {
                haptic(8);
                setOpen(open === i ? -1 : i);
              }}
              className="w-full text-left px-5 py-4"
              data-testid={`recipe-toggle-${i}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-serif text-2xl leading-tight">{r.title}</p>
                  <p className="text-sm text-[#4a5c62] mt-1 italic font-serif">{r.quip}</p>
                </div>
                {r.time && (
                  <span className="shrink-0 flex items-center gap-1 text-xs text-[#4a5c62] bg-white/60 hairline rounded-full px-2.5 py-1">
                    <Clock className="w-3.5 h-3.5" strokeWidth={1.5} /> {r.time}
                  </span>
                )}
              </div>
            </button>

            {open === i && (
              <motion.div className="px-5 pb-5" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} data-testid={`recipe-body-${i}`}>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-white/50 rounded-sm p-3">
                    <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[var(--new)] flex items-center gap-1 mb-1.5">
                      <Check className="w-3.5 h-3.5" strokeWidth={2} /> You have
                    </p>
                    <ul className="text-sm text-[#16242a] space-y-0.5">
                      {(r.have || []).map((h) => <li key={h}>{h}</li>)}
                    </ul>
                  </div>
                  <div className="bg-white/50 rounded-sm p-3">
                    <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[var(--squatter)] flex items-center gap-1 mb-1.5">
                      <ShoppingCart className="w-3.5 h-3.5" strokeWidth={1.8} /> You'll need
                    </p>
                    <ul className="text-sm text-[#16242a] space-y-0.5">
                      {(r.need || []).length ? r.need.map((n) => <li key={n}>{n}</li>) : <li className="text-[#4a5c62]">Nothing — you're set.</li>}
                    </ul>
                  </div>
                </div>
                <ol className="space-y-2">
                  {(r.steps || []).map((s, si) => (
                    <li key={si} className="flex gap-3 text-sm text-[#16242a]">
                      <span className="shrink-0 w-5 h-5 rounded-full bg-[#16242a] text-[#eef5f6] text-[11px] flex items-center justify-center font-semibold">{si + 1}</span>
                      <span className="leading-snug">{s}</span>
                    </li>
                  ))}
                </ol>
                <div className="flex gap-2 mt-5">
                  <button data-testid={`recipe-share-${i}`} onClick={() => setShareRecipe(r)} className="flex-1 btn-ink rounded-sm py-3 text-sm flex items-center justify-center gap-2">
                    <Share2 className="w-4 h-4" strokeWidth={1.5} /> Share
                  </button>
                  <button
                    data-testid={`recipe-add-list-${i}`}
                    onClick={() => toggleSel(i)}
                    className={`flex-1 rounded-sm py-3 text-sm flex items-center justify-center gap-2 transition-colors ${selected.has(i) ? "text-[#eef5f6]" : "glass text-[#16242a]"}`}
                    style={selected.has(i) ? { background: "var(--new)" } : undefined}
                  >
                    {selected.has(i) ? <Check className="w-4 h-4" strokeWidth={2} /> : <Plus className="w-4 h-4" strokeWidth={1.8} />}
                    {selected.has(i) ? "On the list" : "Add to list"}
                  </button>
                </div>
              </motion.div>
            )}
          </motion.div>
        ))}
      </div>

      {/* Shopping list bottom bar */}
      <AnimatePresence>
        {selected.size > 0 && !listOpen && (
          <motion.div
            className="fixed inset-x-0 bottom-0 z-40 flex justify-center px-4 pb-[calc(env(safe-area-inset-bottom)+1rem)]"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
          >
            <button
              data-testid="shopping-list-bar"
              onClick={() => setListOpen(true)}
              className="w-full max-w-md btn-ink rounded-lg py-4 px-5 flex items-center justify-between shadow-[0_16px_40px_-12px_rgba(20,40,45,0.5)]"
            >
              <span className="flex items-center gap-2 font-medium">
                <ShoppingCart className="w-5 h-5" strokeWidth={1.5} /> Shopping list
              </span>
              <span className="text-sm opacity-80">{shoppingItems.length} item{shoppingItems.length !== 1 ? "s" : ""} →</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Shopping list sheet */}
      <AnimatePresence>
        {listOpen && (
          <motion.div
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setListOpen(false)}
            data-testid="shopping-list-sheet"
          >
            <motion.div
              className="w-full max-w-md bg-[#eef5f6]/95 backdrop-blur rounded-t-2xl sm:rounded-lg p-6 pb-[calc(env(safe-area-inset-bottom)+1.5rem)] max-h-[80dvh] overflow-y-auto"
              initial={{ y: 40 }}
              animate={{ y: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-serif text-2xl">Shopping list</h3>
                <button data-testid="shopping-close-btn" onClick={() => setListOpen(false)} className="w-9 h-9 rounded-full glass flex items-center justify-center">
                  <X className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
              <p className="text-sm text-[#4a5c62] mb-4">The gaps across your {selected.size} chosen recipe{selected.size !== 1 ? "s" : ""}, tidied and de-duplicated.</p>
              <ul className="flex flex-col gap-2 mb-5" data-testid="shopping-items">
                {shoppingItems.length ? shoppingItems.map((x) => (
                  <li key={x} className="glass rounded-sm px-4 py-3 text-[#16242a] flex items-center gap-3">
                    <span className="w-4 h-4 rounded-[3px] border-2 border-[#4a5c62]" />
                    {x}
                  </li>
                )) : <li className="text-[#4a5c62]">Nothing to buy — you've got it all in.</li>}
              </ul>
              <button data-testid="shopping-copy-btn" onClick={copyList} className="btn-ink w-full rounded-sm py-4 flex items-center justify-center gap-2">
                <Copy className="w-5 h-5" strokeWidth={1.5} /> Copy the list
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {shareRecipe && <RecipeShareModal recipe={shareRecipe} onClose={() => setShareRecipe(null)} />}
      </AnimatePresence>

      <div className="flex gap-3 mt-8">
        <button data-testid="cook-again-btn" onClick={() => navigate("/cook")} className="flex-1 glass rounded-sm py-3.5 text-sm flex items-center justify-center gap-2 hover:bg-white/50 transition-colors">
          <ChefHat className="w-4 h-4" strokeWidth={1.5} /> Cook again
        </button>
        <button data-testid="recipes-home-btn" onClick={() => navigate("/")} className="flex-1 glass rounded-sm py-3.5 text-sm flex items-center justify-center gap-2 hover:bg-white/50 transition-colors">
          <Camera className="w-4 h-4" strokeWidth={1.5} /> Home
        </button>
      </div>
      {selected.size > 0 && <div className="h-20" aria-hidden="true" />}
    </div>
  );
}
